import icsDB from 'libs/DB/icsDB'
import settings from 'popups/Settings'
import settingSyncService from 'services/settingSync'
import { folderEmitter, instanceEmitter } from '../CalendarState'
import { _removeInstances } from '../EventInstanceManager'
import type { ICSSubscription, ServerSub } from './types'
import { REFRESH_INTERVALS } from './types'
import { cloneDeep, isEqual } from 'lodash'
import { reloadEvents as reloadCalendarEvent, applySync } from '../CalendarSync'
import { reloadTodos } from 'pages/todo/modules/TodoSync'
import {
  _loadSubscriptions,
  _saveSubscriptions,
  _findCalendar,
  _createAppCalendar,
  _upsertCalendar,
  _removeCalendar,
  sanitizeRefreshInterval,
} from './calendarHelpers'
import { _fetchAndParseICS } from './icsFetcher'
import { _replaceCalendarEvents } from './instanceHelper'
import { _setupTimer, _clearTimer } from './timerManager'
/** Late-bound 참조: core.ts에서 refreshSubscription을 바인딩한다 (순환 의존성 방지) */
type RefreshFn = (
  id: string,
  callback?: (success: boolean) => void,
  options?: { force?: boolean },
) => Promise<void>
let _refreshSubscriptionFn: RefreshFn | null = null

export const _bindServerSyncRefreshFn = (fn: RefreshFn): void => {
  _refreshSubscriptionFn = fn
}

/** 서버로 push 중 카운터 (stale 데이터 적용 방지, 연속 push 시 안전) */
let _pushingCount = 0

/** Redux subscribe 해제 함수 */
let _unregisterSettingListener: (() => void) | null = null

/**
 * 동기화 대상 필드만 추출한다 (lastSynced, timezone 제외).
 */
const _toSyncable = (subscriptions: ICSSubscription[]): ServerSub[] => {
  return subscriptions.map(({ lastSynced: _, timezone: __, ...rest }) => rest)
}

/**
 * 로컬 변경을 서버에 push한다 (fire-and-forget).
 * 서버 데이터 적용 중에는 건너뛴다 (피드백 루프 방지).
 *
 * subscriptions를 직접 받아 사용한다.
 * settings.setValue는 Redux Saga를 통해 비동기로 state를 갱신하므로,
 * _loadSubscriptions()로 다시 읽으면 아직 갱신 전 값을 읽을 수 있다.
 */
export const _pushToServer = (subscriptions: ICSSubscription[]): void => {
  if (settingSyncService.isApplyingServerSettings()) return
  const syncable = _toSyncable(subscriptions)
  _pushingCount++
  settingSyncService
    .update({ 'calendar.icsSubscriptions': syncable })
    .catch((err) => {
      console.error('Failed to push ICS subscriptions to server:', err)
    })
    .finally(() => {
      _pushingCount--
    })
}

/**
 * 서버에만 있는 구독을 로컬에 추가한다.
 */
const _addServerOnlySubs = (
  serverMap: Map<string, ServerSub>,
  localMap: Map<string, ICSSubscription>,
  localSubs: ICSSubscription[],
): boolean => {
  let changed = false
  for (const [id, serverSub] of serverMap) {
    if (localMap.has(id)) continue

    const newSub: ICSSubscription = {
      ...serverSub,
      refreshInterval: sanitizeRefreshInterval(serverSub.refreshInterval),
      timezone: null,
      lastSynced: null,
      isSync: serverSub.isSync !== undefined ? serverSub.isSync : true,
    }
    localSubs.push(newSub)
    _upsertCalendar(_createAppCalendar(newSub))
    _setupTimer(newSub)

    if (newSub.isSync) {
      _fetchAndParseICS(newSub)
        .then((result) => {
          if (!result) return
          const calendar = _findCalendar(id)
          if (!calendar) return
          _replaceCalendarEvents(calendar, result.events)

          const freshSubs = _loadSubscriptions()
          const freshSub = freshSubs.find((s) => s.id === id)
          if (freshSub) {
            freshSub.lastSynced = new Date().toISOString()
            _saveSubscriptions(freshSubs)
          }
        })
        .catch((err) => {
          console.error('Failed to fetch ICS for server subscription %s:', id, err)
        })
    }
    changed = true
  }
  return changed
}

/**
 * 로컬에만 있는 구독을 삭제한다. 삭제된 ID 목록을 반환한다.
 */
const _removeLocalOnlySubs = (
  serverMap: Map<string, ServerSub>,
  localMap: Map<string, ICSSubscription>,
): { removed: string[]; changed: boolean } => {
  const removed: string[] = []
  for (const [id] of localMap) {
    if (serverMap.has(id)) continue
    _clearTimer(id)
    _removeCalendar(id)
    icsDB.deleteData(id).catch(() => {})
    removed.push(id)
  }
  return { removed, changed: removed.length > 0 }
}

/**
 * 양쪽에 있는 구독의 메타데이터를 비교하고 변경 시 업데이트한다.
 */
const _updateExistingSubs = (
  serverMap: Map<string, ServerSub>,
  localMap: Map<string, ICSSubscription>,
  localSubs: ICSSubscription[],
): boolean => {
  let changed = false
  for (const [id, serverSub] of serverMap) {
    const localSub = localMap.get(id)
    if (!localSub) continue

    const idx = localSubs.findIndex((s) => s.id === id)
    if (idx < 0) continue

    const resolvedIsSync = serverSub.isSync !== undefined ? serverSub.isSync : localSub.isSync

    const needsUpdate =
      localSub.displayName !== serverSub.displayName ||
      localSub.color !== serverSub.color ||
      localSub.refreshInterval !== serverSub.refreshInterval ||
      localSub.isSync !== resolvedIsSync ||
      localSub.url !== serverSub.url

    if (!needsUpdate) continue

    const refreshIntervalChanged = localSub.refreshInterval !== serverSub.refreshInterval
    const isSyncChanged = localSub.isSync !== resolvedIsSync

    localSubs[idx] = {
      ...localSub,
      ...serverSub,
      refreshInterval: sanitizeRefreshInterval(serverSub.refreshInterval),
      timezone: localSub.timezone,
      isSync: resolvedIsSync,
    }

    const calendar = _findCalendar(id)
    if (calendar) {
      calendar.displayName = serverSub.displayName
      calendar.calendarColor = { _cdata: serverSub.color }
      calendar.isSync = resolvedIsSync

      if (localSub.color !== serverSub.color) {
        Object.values(calendar.events).forEach((e) => {
          e.color = serverSub.color
        })
      }

      if (isSyncChanged && !resolvedIsSync) {
        _removeInstances(calendar.events)
        calendar.events = {}
      }

      _upsertCalendar(calendar)
    }

    if (refreshIntervalChanged || isSyncChanged) {
      const updatedSub = localSubs[idx]
      if (updatedSub.isSync && REFRESH_INTERVALS[updatedSub.refreshInterval] > 0) {
        _setupTimer(updatedSub)
      } else {
        _clearTimer(id)
      }
    }

    const urlChanged = localSub.url !== serverSub.url
    if ((urlChanged || isSyncChanged) && resolvedIsSync) {
      _refreshSubscriptionFn?.(id, () => {}, { force: urlChanged }).catch(() => {})
    }

    changed = true
  }
  return changed
}

/**
 * 서버에서 받은 구독 목록을 로컬에 적용하는 reconcile 함수.
 */
export const applyServerSubscriptions = (serverSubs: ServerSub[], prevICS: ServerSub[]): void => {
  // 이 함수가 호출된 경우는 db에서 subscriptions 관련 데이터가 변경됐을 때이므로 localSubs는 비어있는 값으로 간주한다.
  const localSubs = (prevICS as ICSSubscription[]) || [] // _loadSubscriptions()
  const localMap = new Map(localSubs.map((s) => [s.id, s]))
  const serverMap = new Map(serverSubs.map((s) => [s.id, s]))

  const addedChanged = _addServerOnlySubs(serverMap, localMap, localSubs)
  const { removed, changed: removedChanged } = _removeLocalOnlySubs(serverMap, localMap)
  const updatedChanged = _updateExistingSubs(serverMap, localMap, localSubs)

  if (addedChanged || removedChanged || updatedChanged) {
    const finalSubs = localSubs.filter((s) => !removed.includes(s.id))
    _saveSubscriptions(finalSubs)
    folderEmitter.emit('folder')
    instanceEmitter.emit('instance')
  }
}

/**
 * Redux store를 구독하여 calendar.icsSubscriptions 변경을 감지한다.
 * 변경이 감지되면 applyServerSubscriptions를 실행한다.
 */
export const _registerSettingListener = (): void => {
  if (_unregisterSettingListener) return

  const reduxStore = settings.getStore()

  const { calendar: initCalendar, todo: initTodo } = reduxStore.getState().settings
  // Redux Toolkit(immer)는 state 변경 시 해당 슬라이스의 참조를 새로 생성한다.
  // 따라서 참조 비교(===)만으로 변경 여부를 빠르게 판별할 수 있고,
  // 참조가 동일하면 값도 동일하므로 비용이 큰 isEqual을 건너뛸 수 있다.
  let prevCalendarRef = initCalendar
  let prevCalendar = cloneDeep(initCalendar) ?? {}
  let prevTodoRef = initTodo
  let prevTodo = cloneDeep(initTodo) ?? {}

  _unregisterSettingListener = reduxStore.subscribe(() => {
    if (_pushingCount > 0) return

    const { calendar, todo } = reduxStore.getState().settings

    const needReload = {
      calendar: false,
      todo: false,
    }

    // 참조가 동일하면 값 변경 없음 — isEqual 비교 생략
    if (calendar !== prevCalendarRef) {
      prevCalendarRef = calendar
      if (isEqual(prevCalendar, calendar) !== true) {
        if (isEqual(prevCalendar?.icsSubscriptions, calendar?.icsSubscriptions) === false) {
          applyServerSubscriptions(
            calendar?.icsSubscriptions || [],
            prevCalendar?.icsSubscriptions || [],
          )
        }
        if (isEqual(calendar.calendarSyncTargets, prevCalendar.calendarSyncTargets) === false) {
          applySync(calendar.calendarSyncTargets)
        }
        prevCalendar = cloneDeep(calendar)
        needReload.calendar = true
      }
    }

    if (todo !== prevTodoRef) {
      prevTodoRef = todo
      if (isEqual(prevTodo, todo) !== true) {
        prevTodo = cloneDeep(todo)
        needReload.todo = true
      }
    }

    if (needReload.calendar || needReload.todo) {
      // 서버에서 수신한 변경(notifyListeners 경로)이면 settingSyncService가 이미
      // _applyingServerSettings 플래그를 set한 상태이므로, reload 내부에서 발생하는
      // UPLOAD_SETTINGS는 UPLOAD_SETTINGS saga에서 자동으로 차단된다 (echo 방지).
      // 로컬 사용자 변경(Settings 팝업 OK 등)이면 플래그가 false이므로 reload는 수행되되
      // 후속 UPLOAD_SETTINGS는 정상 통과한다.
      if (needReload.calendar) {
        reloadCalendarEvent(true, true)
      }
      if (needReload.todo) {
        reloadTodos(true, true)
      }
    }
  })
}

/**
 * 동기화 상태를 리셋한다 (로그아웃 시).
 */
export const resetSyncState = (): void => {
  _pushingCount = 0
  if (_unregisterSettingListener) {
    _unregisterSettingListener()
    _unregisterSettingListener = null
  }
  // 서버 설정 적용 중 플래그와 타이머를 즉시 해제하여 재로그인 직후 UPLOAD_SETTINGS가
  // 이전 세션의 잔존 플래그로 차단되지 않도록 한다.
  settingSyncService.cancelApplyingServerSettings()
}
